/**
 * Canonical opening-balance → GL posting (journal_entries + journal_entry_lines).
 * Idempotent per (reference_type, reference_id): one active JE; amount changes void prior and recreate.
 */

import { supabase } from '@/lib/supabase';
import { accountingService, type JournalEntry, type JournalEntryLine } from './accountingService';
import { accountService } from './accountService';
import { defaultAccountsService } from './defaultAccountsService';
import type { AccountCategory } from './chartAccountService';
import { resolveStockMovementValuation, roundStockMoney as roundStockMoneyUtil } from '@/app/utils/stockMovementValuation';

export const OPENING_BALANCE_REFERENCE = {
  CONTACT_AR: 'opening_balance_contact_ar',
  CONTACT_AP: 'opening_balance_contact_ap',
  CONTACT_WORKER: 'opening_balance_contact_worker',
  GL_ACCOUNT: 'opening_balance_account',
  /** One active JE per opening stock_movements row (reference_id = movement id). Dr Inventory / Cr Equity. */
  INVENTORY_OPENING: 'opening_balance_inventory',
} as const;

const MONEY_EPS = 0.02;

function roundMoney(n: number): number {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function normalizeBranchId(branchId?: string | null): string | undefined {
  if (!branchId || branchId === 'all') return undefined;
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(branchId)) return undefined;
  return branchId;
}

async function findAccountIdByCode(companyId: string, code: string): Promise<string | null> {
  const rows = await accountService.getAllAccounts(companyId);
  const c = code.trim();
  const hit = (rows || []).find((a: any) => String(a.code ?? '').trim() === c);
  return hit?.id ?? null;
}

/** Prefer 3000 Owner Capital / Capital; fallback first equity-type account. */
async function resolveOpeningEquityAccountId(companyId: string): Promise<string> {
  await defaultAccountsService.ensureDefaultAccounts(companyId);
  let id = await findAccountIdByCode(companyId, '3000');
  if (id) return id;
  const rows = await accountService.getAllAccounts(companyId);
  const t = (rows || []).find(
    (a: any) =>
      String(a.type ?? '').toLowerCase() === 'equity' &&
      /capital|owner|opening/i.test(String(a.name ?? ''))
  );
  if (t?.id) return t.id as string;
  const anyEq = (rows || []).find((a: any) => String(a.type ?? '').toLowerCase() === 'equity');
  if (anyEq?.id) return anyEq.id as string;
  throw new Error(
    'Opening balance equity account not found (expected code 3000 or an equity account). Run default account setup.'
  );
}

async function findActiveOpeningEntry(
  companyId: string,
  referenceType: string,
  referenceId: string
): Promise<{ id: string; entry_date: string | null } | null> {
  const { data, error } = await supabase
    .from('journal_entries')
    .select('id, entry_date')
    .eq('company_id', companyId)
    .eq('reference_type', referenceType)
    .eq('reference_id', referenceId)
    .or('is_void.is.null,is_void.eq.false')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error && error.code !== 'PGRST116') {
    console.warn('[openingBalanceJournalService] findActiveOpeningEntry:', error.message);
    return null;
  }
  return data?.id ? { id: data.id as string, entry_date: (data as { entry_date?: string | null }).entry_date ?? null } : null;
}

async function patchOpeningEntryDateIfNeeded(
  journalEntryId: string,
  currentEntryDate: string | null | undefined,
  desiredDate: string
): Promise<void> {
  const desired = String(desiredDate || '').trim().slice(0, 10);
  if (!desired || !journalEntryId) return;
  const current = String(currentEntryDate || '').slice(0, 10);
  if (current === desired) return;
  const { error: updErr } = await supabase
    .from('journal_entries')
    .update({ entry_date: desired, updated_at: new Date().toISOString() })
    .eq('id', journalEntryId);
  if (updErr) throw updErr;
}

async function voidJournalEntry(journalEntryId: string): Promise<void> {
  const { error } = await supabase
    .from('journal_entries')
    .update({ is_void: true, updated_at: new Date().toISOString() })
    .eq('id', journalEntryId);
  if (error) throw error;
}

/** Legacy: trigger posted stock_adjustment + expense; opening must use equity instead. */
async function voidMisclassifiedStockAdjustmentJesForMovement(movementId: string): Promise<void> {
  const { data: rows } = await supabase
    .from('journal_entries')
    .select('id')
    .eq('reference_type', 'stock_adjustment')
    .eq('reference_id', movementId)
    .or('is_void.is.null,is_void.eq.false');
  for (const r of rows || []) {
    await voidJournalEntry((r as { id: string }).id);
  }
}

async function resolveInventoryAssetAccountId(companyId: string): Promise<string | null> {
  await defaultAccountsService.ensureDefaultAccounts(companyId);
  let id = await findAccountIdByCode(companyId, '1200');
  if (id) return id;
  const rows = await accountService.getAllAccounts(companyId);
  const hit = (rows || []).find((a: any) => String(a.type ?? '').toLowerCase() === 'inventory');
  return hit?.id ?? null;
}

async function sumLineOnAccount(journalEntryId: string, accountId: string): Promise<{ debit: number; credit: number }> {
  const { data, error } = await supabase
    .from('journal_entry_lines')
    .select('debit, credit')
    .eq('journal_entry_id', journalEntryId)
    .eq('account_id', accountId);
  if (error || !data?.length) return { debit: 0, credit: 0 };
  let debit = 0;
  let credit = 0;
  data.forEach((l: any) => {
    debit += Number(l.debit) || 0;
    credit += Number(l.credit) || 0;
  });
  return { debit: roundMoney(debit), credit: roundMoney(credit) };
}

/**
 * If active JE exists and matches expected primary-account net (debit − credit), keep it.
 * Otherwise void and return false so caller creates a fresh JE.
 */
async function reconcileOrVoidOpeningJe(params: {
  companyId: string;
  referenceType: string;
  referenceId: string;
  primaryAccountId: string;
  /** Expected (debit − credit) on primary account for this opening */
  expectedPrimaryNet: number;
}): Promise<boolean> {
  const existing = await findActiveOpeningEntry(params.companyId, params.referenceType, params.referenceId);
  if (!existing) return false;
  const { debit, credit } = await sumLineOnAccount(existing.id, params.primaryAccountId);
  const net = roundMoney(debit - credit);
  if (Math.abs(net - roundMoney(params.expectedPrimaryNet)) <= MONEY_EPS) return true;
  await voidJournalEntry(existing.id);
  return false;
}

async function postBalancedOpening(params: {
  companyId: string;
  branchId?: string;
  referenceType: string;
  referenceId: string;
  description: string;
  lines: { account_id: string; debit: number; credit: number; description: string }[];
  entryDate: string;
  /** Optional stable entry_no (e.g. INV-OB-{movementPrefix}) */
  entryNo?: string;
}): Promise<void> {
  const totalDebit = roundMoney(params.lines.reduce((s, l) => s + l.debit, 0));
  const totalCredit = roundMoney(params.lines.reduce((s, l) => s + l.credit, 0));
  if (Math.abs(totalDebit - totalCredit) > MONEY_EPS) {
    throw new Error(`Opening balance JE not balanced: debit ${totalDebit} credit ${totalCredit}`);
  }
  if (totalDebit < MONEY_EPS && totalCredit < MONEY_EPS) return;

  const entry = {
    id: '',
    company_id: params.companyId,
    branch_id: params.branchId,
    entry_no: params.entryNo ?? `JE-OB-${Date.now()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`,
    entry_date: params.entryDate.slice(0, 10),
    description: params.description,
    reference_type: params.referenceType,
    reference_id: params.referenceId,
  } as JournalEntry;

  const lines: JournalEntryLine[] = params.lines.map((l) => ({
    id: '',
    journal_entry_id: '',
    account_id: l.account_id,
    debit: l.debit,
    credit: l.credit,
    description: l.description,
  }));

  await accountingService.createEntry(entry, lines);
}

function openingEntryDate(): string {
  return new Date().toISOString().slice(0, 10);
}

async function syncWorkerOperationalBalance(workerContactId: string, amount: number): Promise<void> {
  const { error } = await supabase
    .from('workers')
    .update({ current_balance: roundMoney(amount), updated_at: new Date().toISOString() })
    .eq('id', workerContactId);
  if (error && import.meta.env?.DEV) {
    console.warn('[openingBalanceJournalService] workers.current_balance sync:', error.message);
  }
}

export const openingBalanceJournalService = {
  OPENING_BALANCE_REFERENCE,

  /**
   * Load contact from DB and post/void opening JEs for AR, AP, and worker legs as applicable.
   * @param options.effectiveDate — optional YYYY-MM-DD for new opening JEs (Developer Center apply).
   */
  async syncFromContactRow(contactId: string, options?: { effectiveDate?: string }): Promise<void> {
    const { data: row, error } = await supabase
      .from('contacts')
      .select(
        'id, company_id, branch_id, type, name, opening_balance, supplier_opening_balance'
      )
      .eq('id', contactId)
      .maybeSingle();
    if (error || !row) {
      if (error) console.warn('[openingBalanceJournalService] syncFromContactRow load:', error.message);
      return;
    }

    const companyId = row.company_id as string;
    const branchId = normalizeBranchId(row.branch_id as string | null);
    const type = String(row.type || '').toLowerCase();
    const name = String((row as any).name || 'Contact');

    await defaultAccountsService.ensureDefaultAccounts(companyId);
    const equityId = await resolveOpeningEquityAccountId(companyId);
    // Use customer/supplier sub-ledger accounts when available (AR-CUS0001, AP-SUP0001)
    // Falls back to parent control (1100, 2000) if no sub-ledger exists
    let arId = await findAccountIdByCode(companyId, '1100');
    let apId = await findAccountIdByCode(companyId, '2000');
    if (type === 'customer' || type === 'both') {
      try {
        const { resolveReceivablePostingAccountId } = await import('./partySubledgerAccountService');
        const subId = await resolveReceivablePostingAccountId(companyId, contactId);
        if (subId) arId = subId;
      } catch { /* fallback to parent 1100 */ }
    }
    if (type === 'supplier' || type === 'both') {
      try {
        const { resolvePayablePostingAccountId } = await import('./partySubledgerAccountService');
        const subId = await resolvePayablePostingAccountId(companyId, contactId);
        if (subId) apId = subId;
      } catch { /* fallback to parent 2000 */ }
    }
    const wpId = await findAccountIdByCode(companyId, '2010');
    const waId = await findAccountIdByCode(companyId, '1180');

    const entryDate = (options?.effectiveDate || openingEntryDate()).slice(0, 10);

    // --- Customer / both: AR from opening_balance
    if (type === 'customer' || type === 'both') {
      const raw = roundMoney(Number((row as any).opening_balance) || 0);
      if (Math.abs(raw) < MONEY_EPS) {
        const ex = await findActiveOpeningEntry(companyId, OPENING_BALANCE_REFERENCE.CONTACT_AR, contactId);
        if (ex) await voidJournalEntry(ex.id);
      } else if (arId) {
        const primaryNet = raw > 0 ? raw : -Math.abs(raw); // negative → credit AR
        const ok = await reconcileOrVoidOpeningJe({
          companyId,
          referenceType: OPENING_BALANCE_REFERENCE.CONTACT_AR,
          referenceId: contactId,
          primaryAccountId: arId,
          expectedPrimaryNet: primaryNet,
        });
        if (!ok) {
          const amt = Math.abs(raw);
          const lines =
            raw > 0
              ? [
                  { account_id: arId, debit: amt, credit: 0, description: 'Opening balance — receivable' },
                  { account_id: equityId, debit: 0, credit: amt, description: 'Opening balance — offset (Owner Capital)' },
                ]
              : [
                  { account_id: equityId, debit: amt, credit: 0, description: 'Opening balance — offset (Owner Capital)' },
                  { account_id: arId, debit: 0, credit: amt, description: 'Opening balance — receivable (credit)' },
                ];
          await postBalancedOpening({
            companyId,
            branchId,
            referenceType: OPENING_BALANCE_REFERENCE.CONTACT_AR,
            referenceId: contactId,
            description: `Opening balance — customer AR — ${name}`,
            lines,
            entryDate,
          });
        }
      }
    } else {
      const ex = await findActiveOpeningEntry(companyId, OPENING_BALANCE_REFERENCE.CONTACT_AR, contactId);
      if (ex) await voidJournalEntry(ex.id);
    }

    // --- Supplier / both: AP
    let apSource = 0;
    if (type === 'supplier' || type === 'both') {
      const supOb = (row as any).supplier_opening_balance;
      const ob = (row as any).opening_balance;
      if (type === 'supplier') {
        // Supplier-only: legacy rows may store payable opening in opening_balance.
        // IMPORTANT: explicit 0 in supplier_opening_balance must NOT block fallback — many rows store 0.00 in DB
        // while the real opening lives in opening_balance (UI / import paths).
        const supNum = supOb != null && supOb !== '' ? roundMoney(Number(supOb) || 0) : null;
        if (supNum != null && Math.abs(supNum) >= MONEY_EPS) {
          apSource = supNum;
        } else {
          apSource = roundMoney(Number(ob) || 0);
        }
      } else {
        apSource = roundMoney(Number(supOb) || 0);
      }
    }

    if (type === 'supplier' || type === 'both') {
      if (Math.abs(apSource) < MONEY_EPS) {
        const ex = await findActiveOpeningEntry(companyId, OPENING_BALANCE_REFERENCE.CONTACT_AP, contactId);
        if (ex) await voidJournalEntry(ex.id);
      } else if (apId) {
        // Payable positive = we owe supplier → Cr AP
        const primaryNet = apSource > 0 ? -apSource : Math.abs(apSource); // net on AP: Cr positive → credit − debit negative
        const ok = await reconcileOrVoidOpeningJe({
          companyId,
          referenceType: OPENING_BALANCE_REFERENCE.CONTACT_AP,
          referenceId: contactId,
          primaryAccountId: apId,
          expectedPrimaryNet: primaryNet,
        });
        if (!ok) {
          const amt = Math.abs(apSource);
          const lines =
            apSource > 0
              ? [
                  { account_id: equityId, debit: amt, credit: 0, description: 'Opening balance — offset (Owner Capital)' },
                  { account_id: apId, debit: 0, credit: amt, description: 'Opening balance — payable' },
                ]
              : [
                  { account_id: apId, debit: amt, credit: 0, description: 'Opening balance — payable (debit)' },
                  { account_id: equityId, debit: 0, credit: amt, description: 'Opening balance — offset (Owner Capital)' },
                ];
          await postBalancedOpening({
            companyId,
            branchId,
            referenceType: OPENING_BALANCE_REFERENCE.CONTACT_AP,
            referenceId: contactId,
            description: `Opening balance — supplier AP — ${name}`,
            lines,
            entryDate,
          });
        }
      }
    } else {
      const ex = await findActiveOpeningEntry(companyId, OPENING_BALANCE_REFERENCE.CONTACT_AP, contactId);
      if (ex) await voidJournalEntry(ex.id);
    }

    // --- Worker: opening_balance → 2010 payable if positive, 1180 advance if negative
    if (type === 'worker') {
      const wAmt = roundMoney(Number((row as any).opening_balance) || 0);
      await syncWorkerOperationalBalance(contactId, wAmt);

      if (Math.abs(wAmt) < MONEY_EPS) {
        const ex = await findActiveOpeningEntry(companyId, OPENING_BALANCE_REFERENCE.CONTACT_WORKER, contactId);
        if (ex) await voidJournalEntry(ex.id);
      } else {
        const usePayable = wAmt > 0;
        const primaryId = usePayable ? wpId : waId;
        if (!primaryId) {
          console.warn('[openingBalanceJournalService] Missing 2010/1180 for worker opening');
        } else {
          const primaryNet = usePayable ? -Math.abs(wAmt) : Math.abs(wAmt); // AP: Cr net negative as debit−credit
          const ok = await reconcileOrVoidOpeningJe({
            companyId,
            referenceType: OPENING_BALANCE_REFERENCE.CONTACT_WORKER,
            referenceId: contactId,
            primaryAccountId: primaryId,
            expectedPrimaryNet: primaryNet,
          });
          if (!ok) {
            const amt = Math.abs(wAmt);
            const lines = usePayable
              ? [
                  { account_id: equityId, debit: amt, credit: 0, description: 'Opening balance — offset (Owner Capital)' },
                  { account_id: primaryId, debit: 0, credit: amt, description: 'Opening balance — worker payable' },
                ]
              : [
                  { account_id: primaryId, debit: amt, credit: 0, description: 'Opening balance — worker advance' },
                  { account_id: equityId, debit: 0, credit: amt, description: 'Opening balance — offset (Owner Capital)' },
                ];
            await postBalancedOpening({
              companyId,
              branchId,
              referenceType: OPENING_BALANCE_REFERENCE.CONTACT_WORKER,
              referenceId: contactId,
              description: `Opening balance — worker — ${name}`,
              lines,
              entryDate,
            });
          }
        }
      }
    } else {
      const ex = await findActiveOpeningEntry(companyId, OPENING_BALANCE_REFERENCE.CONTACT_WORKER, contactId);
      if (ex) await voidJournalEntry(ex.id);
    }
  },

  /**
   * Post opening for a chart/GL account (cash/bank/wallet or any COA row).
   * Amount is always treated as absolute. Direction:
   * - `primarySide` when set (debit | credit on the account line)
   * - else category natural: Assets / Expenses / COS → debit; else credit
   */
  async syncChartAccountOpening(params: {
    companyId: string;
    branchId?: string;
    accountId: string;
    accountCode?: string;
    accountName?: string;
    category: AccountCategory;
    openingAmount: number;
    /** GL entry_date for the opening balance JE (defaults to today). */
    entryDate?: string;
    /** Explicit COA side on the account; overrides category natural when set. */
    primarySide?: 'debit' | 'credit';
  }): Promise<void> {
    const amt = roundMoney(Math.abs(params.openingAmount));
    if (amt < MONEY_EPS) {
      const ex = await findActiveOpeningEntry(
        params.companyId,
        OPENING_BALANCE_REFERENCE.GL_ACCOUNT,
        params.accountId
      );
      if (ex) await voidJournalEntry(ex.id);
      return;
    }

    await defaultAccountsService.ensureDefaultAccounts(params.companyId);
    const equityId = await resolveOpeningEquityAccountId(params.companyId);
    const branchId = normalizeBranchId(params.branchId);

    const debitNatural =
      params.category === 'Assets' || params.category === 'Cost of Sales' || params.category === 'Expenses';
    const onDebit =
      params.primarySide === 'debit'
        ? true
        : params.primarySide === 'credit'
          ? false
          : debitNatural;

    const primaryNet = onDebit ? amt : -amt;
    const ok = await reconcileOrVoidOpeningJe({
      companyId: params.companyId,
      referenceType: OPENING_BALANCE_REFERENCE.GL_ACCOUNT,
      referenceId: params.accountId,
      primaryAccountId: params.accountId,
      expectedPrimaryNet: primaryNet,
    });
    if (ok) {
      // Amount/side already match — still allow date-only edits (Edit Account OB date).
      const desiredDate = String(params.entryDate || '').trim().slice(0, 10);
      if (desiredDate) {
        const existing = await findActiveOpeningEntry(
          params.companyId,
          OPENING_BALANCE_REFERENCE.GL_ACCOUNT,
          params.accountId
        );
        if (existing?.id) {
          await patchOpeningEntryDateIfNeeded(existing.id, existing.entry_date, desiredDate);
        }
      }
      return;
    }

    const label = [params.accountCode, params.accountName].filter(Boolean).join(' — ') || params.accountId;
    const lines = onDebit
      ? [
          { account_id: params.accountId, debit: amt, credit: 0, description: `Opening balance — ${label}` },
          { account_id: equityId, debit: 0, credit: amt, description: 'Opening balance — offset (Owner Capital)' },
        ]
      : [
          { account_id: equityId, debit: amt, credit: 0, description: 'Opening balance — offset (Owner Capital)' },
          { account_id: params.accountId, debit: 0, credit: amt, description: `Opening balance — ${label}` },
        ];

    const entryDate =
      String(params.entryDate || '').trim().slice(0, 10) || openingEntryDate();
    await postBalancedOpening({
      companyId: params.companyId,
      branchId,
      referenceType: OPENING_BALANCE_REFERENCE.GL_ACCOUNT,
      referenceId: params.accountId,
      description: `Opening balance — account ${label}`,
      lines,
      entryDate,
    });
  },

  /**
   * Canonical GL for `stock_movements` with reference_type = opening_balance.
   * Dr Inventory (1200 or type inventory) / Cr Owner Capital (3000). reference_id = movement id.
   * Voids active `stock_adjustment` JEs on the same movement (legacy wrong contra account).
   *
   * Returns a result object so callers can aggregate accurate diagnostic counts.
   * - posted: a new JE was created
   * - kept: an existing JE with correct amount was kept (idempotent)
   * - skippedZeroCost: movement has zero cost even after product cost fallback
   * - amount: the Rs. amount posted (or kept)
   */
  async syncInventoryOpeningFromStockMovementId(
    movementId: string,
    opts?: { suppressNotify?: boolean }
  ): Promise<{
    posted: boolean;
    kept: boolean;
    skippedZeroCost: boolean;
    amount: number;
  }> {
    const SKIP = { posted: false, kept: false, skippedZeroCost: false, amount: 0 };
    const { data: m, error } = await supabase.from('stock_movements').select('*').eq('id', movementId).maybeSingle();
    if (error || !m) {
      if (error) console.warn('[openingBalanceJournalService] syncInventoryOpening load movement:', error.message);
      return SKIP;
    }
    if (String(m.reference_type || '').toLowerCase().trim() !== 'opening_balance') return SKIP;
    // reference_type='opening_balance' is the sole discriminator. Do NOT gate on movement_type —
    // legacy rows may have any movement_type (null, 'adjustment', 'purchase', etc.) and must all be posted.

    const companyId = m.company_id as string;
    await voidMisclassifiedStockAdjustmentJesForMovement(movementId);

    const valuation = await resolveStockMovementValuation(m);
    let amt = roundMoney(valuation.amount);

    if (valuation.usedProductFallback && amt > MONEY_EPS) {
      await supabase
        .from('stock_movements')
        .update({
          unit_cost: valuation.unitCost,
          total_cost: roundStockMoneyUtil(amt),
          movement_type: 'adjustment',
        })
        .eq('id', movementId);
      console.info(
        `[openingBalanceJournalService] Inventory OB cost fallback: movement ${movementId} used product cost_price ${valuation.unitCost} → Rs.${amt}`
      );
    }

    const invId = await resolveInventoryAssetAccountId(companyId);
    if (!invId) {
      console.warn(
        '[openingBalanceJournalService] No inventory account (code 1200 or type inventory) for company',
        companyId
      );
      return SKIP;
    }

    await defaultAccountsService.ensureDefaultAccounts(companyId);
    const equityId = await resolveOpeningEquityAccountId(companyId);
    const branchId = normalizeBranchId(m.branch_id as string | null);
    const entryDate = String(m.created_at || new Date().toISOString()).slice(0, 10);

    if (amt < MONEY_EPS) {
      // Truly zero cost even after fallback — void any stale JE and skip
      const ex = await findActiveOpeningEntry(companyId, OPENING_BALANCE_REFERENCE.INVENTORY_OPENING, movementId);
      if (ex) await voidJournalEntry(ex.id);
      if (!opts?.suppressNotify) {
        try {
          const { notifyAccountingEntriesChanged } = await import('@/app/lib/accountingInvalidate');
          notifyAccountingEntriesChanged();
        } catch {
          /* ignore */
        }
      }
      return { posted: false, kept: false, skippedZeroCost: true, amount: 0 };
    }

    const ok = await reconcileOrVoidOpeningJe({
      companyId,
      referenceType: OPENING_BALANCE_REFERENCE.INVENTORY_OPENING,
      referenceId: movementId,
      primaryAccountId: invId,
      expectedPrimaryNet: amt,
    });
    if (ok) {
      if (!opts?.suppressNotify) {
        try {
          const { notifyAccountingEntriesChanged } = await import('@/app/lib/accountingInvalidate');
          notifyAccountingEntriesChanged();
        } catch {
          /* ignore */
        }
      }
      return { posted: false, kept: true, skippedZeroCost: false, amount: amt };
    }

    let productLabel = '';
    try {
      const { data: p } = await supabase.from('products').select('sku, name').eq('id', m.product_id).maybeSingle();
      if (p) productLabel = `${(p as { sku?: string }).sku || ''} ${(p as { name?: string }).name || ''}`.trim();
    } catch {
      /* ignore */
    }
    const vid = (m as { variation_id?: string | null }).variation_id;
    if (vid) {
      try {
        const { data: pv } = await supabase
          .from('product_variations')
          .select('sku, attributes')
          .eq('id', vid)
          .maybeSingle();
        if (pv) {
          const vsku = String((pv as { sku?: string }).sku || '').trim();
          const attrs = (pv as { attributes?: unknown }).attributes;
          let summary = '';
          if (attrs && typeof attrs === 'object' && !Array.isArray(attrs)) {
            summary = Object.entries(attrs as Record<string, unknown>)
              .filter(([k]) => !String(k).startsWith('__erp'))
              .map(([k, val]) => `${k}:${String(val ?? '').trim()}`)
              .filter((s) => !s.endsWith(':'))
              .join(' · ');
          }
          const varBit = [vsku, summary].filter(Boolean).join(' — ');
          if (varBit) productLabel = productLabel ? `${productLabel} — ${varBit}` : varBit;
        }
      } catch {
        /* ignore */
      }
    }

    await postBalancedOpening({
      companyId,
      branchId,
      referenceType: OPENING_BALANCE_REFERENCE.INVENTORY_OPENING,
      referenceId: movementId,
      description: `Opening inventory${productLabel ? ` — ${productLabel}` : ''}`,
      lines: [
        { account_id: invId, debit: amt, credit: 0, description: 'Opening inventory (asset)' },
        { account_id: equityId, debit: 0, credit: amt, description: 'Opening inventory offset (Owner Capital)' },
      ],
      entryDate,
      entryNo: `INV-OB-${String(movementId).replace(/-/g, '').slice(0, 12)}`,
    });
    return { posted: true, kept: false, skippedZeroCost: false, amount: amt };
  },

  /**
   * Company-scoped repair: normalize supplier-only rows that stored payables in `opening_balance`
   * while `supplier_opening_balance` is zero, then re-sync opening journals for all supplier/both contacts.
   * Idempotent: second run is mostly no-ops (reconcileOrVoidOpeningJe keeps matching JEs).
   * Does not change type=`both` balances (AR stays on opening_balance; AP stays on supplier_opening_balance).
   */
  async repairCompanySupplierOpeningBalances(companyId: string): Promise<{
    companyId: string;
    supplierOnlyNormalized: number;
    contactsResynced: number;
  }> {
    await defaultAccountsService.ensureDefaultAccounts(companyId);

    let supplierOnlyNormalized = 0;

    const { data: supplierRows, error: supErr } = await supabase
      .from('contacts')
      .select('id, opening_balance, supplier_opening_balance')
      .eq('company_id', companyId)
      .eq('type', 'supplier');
    if (supErr) throw supErr;

    for (const row of supplierRows || []) {
      const ob = roundMoney(Number((row as { opening_balance?: number }).opening_balance) || 0);
      const sup = roundMoney(Number((row as { supplier_opening_balance?: number }).supplier_opening_balance) || 0);
      if (Math.abs(sup) >= MONEY_EPS || ob <= MONEY_EPS) continue;

      const { error: upErr } = await supabase
        .from('contacts')
        .update({
          supplier_opening_balance: ob,
          opening_balance: 0,
        })
        .eq('id', (row as { id: string }).id);
      if (!upErr) supplierOnlyNormalized += 1;
    }

    const { data: syncRows, error: listErr } = await supabase
      .from('contacts')
      .select('id')
      .eq('company_id', companyId)
      .in('type', ['supplier', 'both']);
    if (listErr) throw listErr;

    let contactsResynced = 0;
    for (const r of syncRows || []) {
      const id = (r as { id: string }).id;
      await this.syncFromContactRow(id);
      contactsResynced += 1;
    }

    return { companyId, supplierOnlyNormalized, contactsResynced };
  },

  /**
   * Sync opening_balance_inventory JEs for all opening stock movements in a company.
   * One active JE per movement (reference_id = movement id). Idempotent / safe to re-run.
   */
  async syncInventoryOpeningBalancesForCompany(
    companyId: string,
    opts?: { suppressNotify?: boolean; excludeMovementIds?: string[] }
  ): Promise<{
    synced: number;
    posted: number;
    kept: number;
    skippedZeroCost: number;
    totalValue: number;
    errors: string[];
  }> {
    const exclude = new Set(opts?.excludeMovementIds ?? []);
    const errors: string[] = [];
    let synced = 0;
    let posted = 0;
    let kept = 0;
    let skippedZeroCost = 0;
    let totalValue = 0;

    await defaultAccountsService.ensureDefaultAccounts(companyId);

    const { data: movements, error } = await supabase
      .from('stock_movements')
      .select('id')
      .eq('company_id', companyId)
      .eq('reference_type', 'opening_balance');
    if (error) throw error;

    for (const m of movements || []) {
      const id = (m as { id: string }).id;
      if (exclude.has(id)) continue;
      try {
        const result = await this.syncInventoryOpeningFromStockMovementId(id, {
          suppressNotify: opts?.suppressNotify,
        });
        synced++;
        if (result.posted) {
          posted++;
          totalValue = roundMoney(totalValue + result.amount);
        } else if (result.kept) {
          kept++;
          totalValue = roundMoney(totalValue + result.amount);
        } else if (result.skippedZeroCost) {
          skippedZeroCost++;
        }
      } catch (e: unknown) {
        errors.push(e instanceof Error ? e.message : String(e));
      }
    }

    if (!opts?.suppressNotify && (posted > 0 || kept > 0)) {
      try {
        const { notifyAccountingEntriesChanged } = await import('@/app/lib/accountingInvalidate');
        notifyAccountingEntriesChanged();
      } catch {
        /* ignore */
      }
    }

    return { synced, posted, kept, skippedZeroCost, totalValue, errors };
  },

  /**
   * Sync opening balance journal entries for ALL contacts in a company that have
   * opening_balance > 0 or supplier_opening_balance > 0.
   * Idempotent: syncFromContactRow() uses reconcileOrVoidOpeningJe() internally.
   */
  async syncAllContactOpeningBalances(companyId: string): Promise<{
    totalContacts: number;
    synced: number;
    subledgersCreated: number;
    /** @deprecated Use inventoryJEsPosted + inventoryJEsKept for accurate counts */
    inventoryMovementsSynced: number;
    inventoryJEsPosted: number;
    inventoryJEsKept: number;
    inventoryZeroCostSkipped: number;
    inventoryTotalValue: number;
    errors: string[];
  }> {
    await defaultAccountsService.ensureDefaultAccounts(companyId);

    // --- 1. Sync contact opening balance journal entries
    const { data: rows, error } = await supabase
      .from('contacts')
      .select('id, name, type, opening_balance, supplier_opening_balance')
      .eq('company_id', companyId)
      .or('opening_balance.gt.0,supplier_opening_balance.gt.0');
    if (error) throw error;

    let synced = 0;
    let subledgersCreated = 0;
    const errors: string[] = [];

    for (const row of rows || []) {
      try {
        await this.syncFromContactRow((row as { id: string }).id);
        synced++;
      } catch (e: any) {
        errors.push(`${(row as { name: string }).name}: ${e?.message || String(e)}`);
      }
    }

    // --- 2. Ensure party sub-ledger accounts exist for all contacts
    try {
      const { data: allContacts } = await supabase
        .from('contacts')
        .select('id, type')
        .eq('company_id', companyId)
        .in('type', ['customer', 'supplier', 'both']);
      const { ensurePartySubledgersForContact } = await import('./partySubledgerAccountService');
      for (const c of allContacts || []) {
        try {
          await ensurePartySubledgersForContact(companyId, (c as { id: string }).id, (c as { type: string }).type);
          subledgersCreated++;
        } catch { /* already exists or column missing — skip */ }
      }
    } catch (e: any) {
      errors.push(`Sub-ledger creation: ${e?.message || String(e)}`);
    }

    // --- 3. Sync inventory opening stock movements to GL
    let inventoryMovementsSynced = 0;
    let inventoryJEsPosted = 0;
    let inventoryJEsKept = 0;
    let inventoryZeroCostSkipped = 0;
    let inventoryTotalValue = 0;
    try {
      const inv = await this.syncInventoryOpeningBalancesForCompany(companyId, { suppressNotify: true });
      inventoryMovementsSynced = inv.synced;
      inventoryJEsPosted = inv.posted;
      inventoryJEsKept = inv.kept;
      inventoryZeroCostSkipped = inv.skippedZeroCost;
      inventoryTotalValue = inv.totalValue;
      errors.push(...inv.errors.map((e) => `Inventory movement: ${e}`));
      console.info(
        `[openingBalanceJournalService] Inventory OB sync complete: posted=${inventoryJEsPosted} kept=${inventoryJEsKept} zeroCostSkipped=${inventoryZeroCostSkipped} totalValue=Rs.${inventoryTotalValue}`
      );
    } catch (e: any) {
      errors.push(`Inventory sync: ${e?.message || String(e)}`);
    }

    return {
      totalContacts: (rows || []).length,
      synced,
      subledgersCreated,
      inventoryMovementsSynced,
      inventoryJEsPosted,
      inventoryJEsKept,
      inventoryZeroCostSkipped,
      inventoryTotalValue,
      errors,
    };
  },

  /**
   * Post a balanced adjustment JE for opening balance gap (does not void existing opening JE).
   * Developer Center controlled repair only.
   */
  async createContactOpeningAdjustment(params: {
    companyId: string;
    contactId: string;
    entityType: 'contact_ar' | 'contact_ap' | 'contact_worker';
    gap: number;
    effectiveDate: string;
    contactName?: string;
  }): Promise<{ entryDate: string; lines: { account_id: string; debit: number; credit: number; description: string }[] }> {
    const { companyId, contactId, entityType, gap, effectiveDate, contactName } = params;
    const amt = roundMoney(Math.abs(gap));
    if (amt < MONEY_EPS) throw new Error('Adjustment amount too small');

    await defaultAccountsService.ensureDefaultAccounts(companyId);
    const equityId = await resolveOpeningEquityAccountId(companyId);
    const entryDate = effectiveDate.slice(0, 10);
    const name = contactName || 'Contact';

    let primaryId: string | null = null;
    if (entityType === 'contact_ar') {
      primaryId = await findAccountIdByCode(companyId, '1100');
      try {
        const { resolveReceivablePostingAccountId } = await import('./partySubledgerAccountService');
        const subId = await resolveReceivablePostingAccountId(companyId, contactId);
        if (subId) primaryId = subId;
      } catch { /* parent AR */ }
    } else if (entityType === 'contact_ap') {
      primaryId = await findAccountIdByCode(companyId, '2000');
      try {
        const { resolvePayablePostingAccountId } = await import('./partySubledgerAccountService');
        const subId = await resolvePayablePostingAccountId(companyId, contactId);
        if (subId) primaryId = subId;
      } catch { /* parent AP */ }
    } else {
      primaryId = await findAccountIdByCode(companyId, '2010') || (await findAccountIdByCode(companyId, '1180'));
    }
    if (!primaryId) throw new Error('Primary opening account not found');

    const refType = `opening_balance_adjustment_${entityType.replace('contact_', '')}`;
    const lines =
      entityType === 'contact_ap'
        ? gap > 0
          ? [
              { account_id: equityId, debit: amt, credit: 0, description: 'Opening adjustment — equity offset' },
              { account_id: primaryId, debit: 0, credit: amt, description: 'Opening adjustment — payable' },
            ]
          : [
              { account_id: primaryId, debit: amt, credit: 0, description: 'Opening adjustment — payable' },
              { account_id: equityId, debit: 0, credit: amt, description: 'Opening adjustment — equity offset' },
            ]
        : gap > 0
          ? [
              { account_id: primaryId, debit: amt, credit: 0, description: 'Opening adjustment — receivable' },
              { account_id: equityId, debit: 0, credit: amt, description: 'Opening adjustment — equity offset' },
            ]
          : [
              { account_id: equityId, debit: amt, credit: 0, description: 'Opening adjustment — equity offset' },
              { account_id: primaryId, debit: 0, credit: amt, description: 'Opening adjustment — receivable' },
            ];

    await postBalancedOpening({
      companyId,
      referenceType: refType,
      referenceId: contactId,
      description: `Opening balance adjustment — ${name} (${entityType})`,
      lines,
      entryDate,
    });

    return { entryDate, lines };
  },
};
